import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogTitle,
    Grid,
    TextField,
    Typography,
    InputAdornment,
    FormControlLabel,
    Switch,
} from "@mui/material";

import { getAuth } from "firebase/auth";
import { db, storage } from "../../firebase";
import { getDoc, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import imageCompression from "browser-image-compression";

import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

function VideoCreate() {
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const [user, setUser] = useState("");

    const [imgUrl, setImgUrl] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [complete, setComplete] = useState(false);
    const [episode, setEpisode] = useState(1);
    const [url, setUrl] = useState("");
    const [release, setRelease] = useState(false);

    const fileInputRef = useRef(null);
    const handleAvaterOpen = () => {
        fileInputRef.current.value = "";
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const fileSrc = event.target.files[0];
        const fileURL = event.target.files;
        if (fileURL) {
            const supportedImageTypes = ["image/jpeg", "image/jpg", "image/png"];
            if (!supportedImageTypes.includes(fileSrc.type)) {
                console.log("Error: サポートされていない拡張子です");
                return;
            } else {
                const options = {
                    maxSizeMB: 2,
                    maxWidthOrHeight: 1280,
                    useWebWorker: true,
                    fileType: fileURL.type,
                    quality: 0.85,
                };

                const compressedFile = await imageCompression(fileSrc, options);
                const reader = new FileReader();
                reader.readAsDataURL(compressedFile);
                reader.onloadend = () => {
                    setImgUrl(reader.result);
                };
            }
        }
    };

    const handleTitleChange = (event) => {
        setTitle(event.target.value);
    };

    const handleDescriptionChange = (event) => {
        setDescription(event.target.value);
    };

    const handleEpisodeChange = (event) => {
        setEpisode(event.target.value);
    };

    const handleCompleteChange = () => {
        setComplete(!complete);
    };

    const handleUrlChange = (event) => {
        setUrl(event.target.value);
    };

    const handleReleaseChange = () => {
        setRelease(!release);
    };

    const handleCreateClick = async () => {
        let randomId;
        let videoSnap;
        do {
            randomId = Math.random().toString(36).substring(2, 12);

            const videoRef = doc(db, "video", randomId);
            videoSnap = await getDoc(videoRef);
        } while (videoSnap.exists());

        let uploadImgURL = "";

        if (imgUrl) {
            try {
                const strageRef = ref(storage, `video/${randomId}`);

                const blobFile = await fetch(imgUrl).then((res) => res.blob());

                await uploadBytes(strageRef, blobFile);
                uploadImgURL = await getDownloadURL(strageRef);
            } catch (error) {
                console.error("Error uploading img: ", error);
            }
        }

        try {
            const documentRef = doc(db, "video", randomId);
            await setDoc(documentRef, {
                img: uploadImgURL,
                id: randomId,
                title: title,
                description: description,
                complete: complete,
                episode: episode,
                url: url,
                release: release,
                auther: user,
                createAt: serverTimestamp(),
                updateAt: serverTimestamp(),
            });
            setSaveOpen(true);
        } catch (error) {
            console.error("Error creating video: ", error);
        }
    };

    const [saveOpen, setSaveOpen] = useState(false);
    const handleSaved = () => {
        window.scrollTo(0, 0);
        navigate("/video", { replace: true });
    };

    useEffect(() => {
        const unsubscribe = getAuth().onAuthStateChanged((userLogin) => {
            if (userLogin) {
                const uid = userLogin.uid;
                setUser(uid);
                setLoading(false);
            } else {
                window.scrollTo(0, 0);
                navigate("/login", { replace: true });
            }
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <Box
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div>
            <Helmet>
                <title>YouTubeLink Create - SOG Member Site</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Container maxWidth="1280" sx={{ display: { xs: { maxWidth: 0 } }, maxWidth: 1280 }}>
                <Container>
                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ mt: 10 }}
                    >
                        <Typography variant="h6">YouTubeリンク作成</Typography>
                    </Box>

                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ m: 3 }}
                    >
                        <Box
                            sx={{
                                minWidth: 350,
                                minHeight: 196.875,
                                border: "1px solid",
                                borderColor: "primary.main",
                                borderRadius: 1,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: "pointer",
                                objectFit: "contain",
                            }}
                            onClick={handleAvaterOpen}
                        >
                            <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} ref={fileInputRef} />

                            {imgUrl ? (
                                <img
                                    src={imgUrl}
                                    alt="uploaded"
                                    style={{
                                        maxWidth: 348,
                                        maxHeight: 195.75,
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        color: "text.secondary",
                                    }}
                                    type="file"
                                    onChange={handleAvaterOpen}
                                >
                                    サムネイルをアップロード
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ m: 3 }}
                    >
                        <TextField variant="filled" label="タイトル" value={title} onChange={handleTitleChange} sx={{ minWidth: 350 }} />
                    </Box>

                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ m: 3 }}
                    >
                        <TextField
                            variant="filled"
                            label="あらすじ"
                            value={description}
                            onChange={handleDescriptionChange}
                            multiline
                            rows={5}
                            sx={{ minWidth: 350 }}
                        />
                    </Box>

                    <Box sx={{ mt: 3 }} style={{ display: "flex", justifyContent: "center" }}>
                        <Grid container sx={{ maxWidth: 350, minWidth: 350 }}>
                            <Grid item xs={7} sm={7}>
                                <TextField
                                    sx={{
                                        input: { textAlign: "right" },
                                        "& .MuiInputBase-input": {
                                            display: "flex",
                                            height: "100%", // 入力値の高さを100%に設定
                                            transform: "translateY(-17%)", // 入力値を上に10%移動
                                        },
                                    }}
                                    value={episode}
                                    variant="filled"
                                    onChange={handleEpisodeChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment
                                                position="start"
                                                sx={{
                                                    height: "100%", // プレフィックスの高さを100%に設定
                                                    display: "flex",
                                                    transform: "translateY(-35%)", // プレフィックスを上に10%移動
                                                }}
                                            >
                                                全
                                            </InputAdornment>
                                        ),
                                        endAdornment: <InputAdornment position="end">話</InputAdornment>,
                                        type: "number",
                                    }}
                                    disabled={!complete}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={5}
                                sm={5}
                                style={{
                                    display: "flex",
                                    justifyContent: "right",
                                    alignItems: "center",
                                }}
                            >
                                <FormControlLabel
                                    checked={complete}
                                    onChange={handleCompleteChange}
                                    label="完結済み"
                                    labelPlacement="start"
                                    control={<Switch sx={{ ml: 2 }} />}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ mt: 3 }} style={{ display: "flex", justifyContent: "center" }}>
                        <Grid container sx={{ maxWidth: 350, minWidth: 350 }}>
                            <Grid item xs={7} sm={7}>
                                <TextField value={url} variant="filled" label="YouTube URL" onChange={handleUrlChange} />
                            </Grid>
                            <Grid
                                item
                                xs={5}
                                sm={5}
                                style={{
                                    display: "flex",
                                    justifyContent: "right",
                                    alignItems: "center",
                                }}
                            >
                                <FormControlLabel
                                    checked={release}
                                    onChange={handleReleaseChange}
                                    label="公開中"
                                    labelPlacement="start"
                                    control={<Switch sx={{ ml: 2 }} />}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ mt: 5, mb: 2 }}
                    >
                        <Button variant="contained" onClick={handleCreateClick} size="large" sx={{ width: "350px" }}>
                            作成
                        </Button>
                    </Box>
                </Container>
            </Container>

            <Dialog open={saveOpen}>
                <DialogTitle
                    sx={{
                        minWidth: 263,
                        justifyContent: "center",
                        display: "flex",
                    }}
                >
                    作成しました
                </DialogTitle>
                <DialogActions sx={{ justifyContent: "center", display: "flex", mb: 2 }}>
                    <Button variant="contained" sx={{ width: "100px" }} onClick={handleSaved}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default VideoCreate;
